import { noOp } from 'common/Constants';
import { useEffect, useRef, useState } from 'react';
import { PortionType, PreviousQuantityType } from './types';

const usePrevious = (value: number) => {
  const [storedValue, setStoredValue] = useState(value);
  const [storedPreviousValue, setStoredPreviousValue] = useState(value);
  const [isChanging, setIsChanging] = useState(false);
  const [hasChanged, setHasChanged] = useState(true);
  const hasChangedRef = useRef(0);

  useEffect(() => {
    if (!isChanging && storedValue !== value) {
      window.clearTimeout(hasChangedRef.current);

      setStoredPreviousValue(storedValue);
      setIsChanging(true);
      setHasChanged(true);
      setStoredValue(value);
      setIsChanging(false);

      // eslint-disable-next-line functional/immutable-data -- Animate only changed value.
      hasChangedRef.current = window.setTimeout(() => {
        setHasChanged(false);
      }, 250);
    }
  }, [isChanging, storedValue, value]);

  useEffect(
    () => () => {
      window.clearTimeout(hasChangedRef.current);
    },
    []
  );

  return {
    currentValue: storedValue,
    hasChanged,
    value: storedPreviousValue ?? 0,
  };
};

export const usePreviousQuantity = (props: PreviousQuantityType) => {
  const { quantity } = props;
  const { currentValue: currentQuantity, hasChanged, value: previousQuantity } = usePrevious(quantity);
  const isGreater = quantity > previousQuantity;
  return { currentQuantity, hasChanged, isGreater, quantity, previousQuantity };
};

export const usePortion = (props: PortionType) => {
  const {
    autoHide = false,
    compact = false,
    className,
    decrementQuantity = noOp,
    incrementQuantity = noOp,
    preventDefaultOnClick = false,
    quantity = 1,
    maxQuantity = 1,
    minQuantity = 1,
    minimizeOnZeroQuantity = false,
    showBorderMinimized = false,
  } = props;

  const [isExpanded, setIsExpanded] = useState(!autoHide);
  const isMax = quantity >= (maxQuantity ?? 1);
  const isMin = quantity <= (minQuantity ?? 1);
  const minimize = ((autoHide || minimizeOnZeroQuantity) && quantity < 1) || !isExpanded;
  const expandRef = useRef(0);

  const showButtonValue = autoHide && minimize && quantity !== 0;

  const expand = () => {
    window.clearTimeout(expandRef.current);
    setIsExpanded(true);

    // eslint-disable-next-line functional/immutable-data -- Auto close animation
    expandRef.current = window.setTimeout(() => {
      if (autoHide) {
        setIsExpanded(false);
      }
    }, 3000);
  };

  useEffect(
    () => () => {
      window.clearTimeout(expandRef.current);
    },
    []
  );

  return {
    autoHide,
    containerProps: {
      className,
      compact,
      onClick: expand,
      highlight: showButtonValue,
      minimize,
      showBorderMinimized,
    },
    decrementButtonProps: {
      disabled: isMin,
      $minimize: minimize,
      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (preventDefaultOnClick) {
          expand();
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
        }
        decrementQuantity();
      },
    },
    incrementButtonProps: {
      disabled: isMax,
      onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (preventDefaultOnClick) {
          expand();
          e.preventDefault();
          e.stopPropagation();
          e.nativeEvent.stopImmediatePropagation();
        }
        if (isExpanded || quantity === 0) {
          incrementQuantity();
        }
      },
    },
    isMax,
    isMin,
    quantity: maxQuantity === 0 ? 0 : quantity,
    maxQuantity,
    minimize,
    minQuantity,
    showButtonValue,
  };
};
